export interface PlaceholderFaceImage {
    id?: number;
    imageUrl?: string;
    image_url?: string;
    name?: string;
}

export interface PlaceholderFace {
    id: number;
    name: string;
    category: string;
    /** Cabinets.style: 1 Base (floor), 3 Upper (wall) */
    style?: number;
    is_resizable?: boolean;
    height: number;
    /** Resize bounds in mm. Null/absent means unbounded on that end. */
    min_height?: number | null;
    max_height?: number | null;
    width: number;
    min_width?: number | null;
    max_width?: number | null;
    depth: number;
    min_depth?: number | null;
    max_depth?: number | null;
    image_id?: number | null;
    image?: PlaceholderFaceImage | null;
    /** Public GLB path for RoomPlanner, e.g. /templates/3D/models/placeholders/fridge.glb */
    template?: string | null;
}

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
    typeof value === 'object' && value !== null;

const toPublicImageUrl = (url: string): string => {
    const trimmed = url.trim();
    if (!trimmed) return '';
    const lower = trimmed.toLowerCase();
    if (
        lower.startsWith('http://') ||
        lower.startsWith('https://') ||
        trimmed.startsWith('//') ||
        trimmed.startsWith('/') ||
        lower.startsWith('data:') ||
        lower.startsWith('blob:')
    ) {
        return trimmed;
    }
    return `/${trimmed}`;
};

const pickImageUrl = (raw: UnknownRecord): string => {
    const candidates = [
        raw.imageUrl,
        raw.image_url,
        raw.url,
        raw.path,
        raw.filepath,
        raw.filename,
        raw.file,
    ];
    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) {
            return toPublicImageUrl(candidate);
        }
    }
    return '';
};

/**
 * Unwrap CakePHP / admin shapes: array, `{0: image}`, or nested `Image`.
 * @param {unknown} image Raw image field
 * @return {UnknownRecord | string | null} First usable image record or path
 */
const unwrapImage = (image: unknown): UnknownRecord | string | null => {
    if (image == null || image === '') return null;
    if (typeof image === 'string') return image;
    if (Array.isArray(image)) return unwrapImage(image[0]);
    if (!isRecord(image)) return null;

    if (!pickImageUrl(image) && isRecord(image['0'])) {
        return unwrapImage(image['0']);
    }
    if (!pickImageUrl(image) && isRecord(image.Image)) {
        return unwrapImage(image.Image);
    }
    if (!pickImageUrl(image) && isRecord(image.image)) {
        return unwrapImage(image.image);
    }

    return image;
};

/**
 * API may send `image` as an object, a one-item array, or a path string.
 * @param {unknown} image Raw image field
 * @return {PlaceholderFaceImage | null} First image with a usable URL
 */
export const normalizePlaceholderFaceImage = (
    image: unknown
): PlaceholderFaceImage | null => {
    const raw = unwrapImage(image);
    if (typeof raw === 'string') {
        const imageUrl = toPublicImageUrl(raw);
        return imageUrl ? {imageUrl, image_url: raw} : null;
    }
    if (!raw) return null;

    const imageUrl = pickImageUrl(raw);
    if (!imageUrl) return null;

    return {
        id: typeof raw.id === 'number' ? raw.id : undefined,
        name: typeof raw.name === 'string' ? raw.name : undefined,
        imageUrl,
        image_url: typeof raw.image_url === 'string' ? raw.image_url : imageUrl,
    };
};

/**
 * Catalog thumbnail URL for a placeholder face, or empty when none.
 * @param {PlaceholderFace} face Catalog face
 * @return {string} Public image URL
 */
export const getPlaceholderFaceImageUrl = (face: PlaceholderFace): string =>
    face.image?.imageUrl || face.image?.image_url || '';
